/*
  File: src/components/product/RecommendationsCarousel.jsx
  Purpose: Horizontal carousel for recommended products with arrow controls.
  Props:
  - items: Array<{ id, imageUrl?, name, displayName?, price, currency }>
  - onItemClick(item), onView?(item)
*/
import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

/**
 * RecommendationsCarousel
 * Horizontal scroll list with gradient fades and arrow controls.
 */
function RecommendationsCarousel({ items, onItemClick, onView }) {
    const scrollRef = useRef(null);
    const scrollByAmount = (dir) => {
        const el = scrollRef.current;
        if (!el) return;
        const amount = Math.floor(el.clientWidth * 0.9) * dir;
        el.scrollBy({ left: amount, behavior: 'smooth' });
    };

    if (!items || items.length === 0) return null;

    return (
        <div className="relative pt-8">
            <div className="absolute right-0 top-0 z-20 mr-1 inline-flex items-center gap-2">
                <button aria-label="Previous" onClick={() => scrollByAmount(-1)} className="btn-ghost h-9 w-9 rounded-full">
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button aria-label="Next" onClick={() => scrollByAmount(1)} className="btn-ghost h-9 w-9 rounded-full">
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
            <div ref={scrollRef} className="mt-4 flex gap-4 overflow-x-auto snap-x snap-mandatory pl-1 pr-1 scrollbar-hide bg-transparent" style={{ WebkitOverflowScrolling: 'touch' }}>
                {items.map(r => (
                    <div key={r.id} className="snap-start shrink-0 w-40 sm:w-44 md:w-48 lg:w-52 bg-white/5 rounded-xl border border-white/10 overflow-hidden cursor-pointer transition-transform duration-200 hover:-translate-y-0.5 hover:border-white/20" onClick={() => onItemClick(r)}>
                        <div className="relative aspect-[4/5]" style={{ background: 'rgba(255,255,255,0.6)' }}>
                            {r.imageUrl ? (
                                <img
                                    src={r.imageUrl}
                                    alt={r.displayName || r.name}
                                    className="w-full h-full object-contain"
                                    loading="lazy"
                                    decoding="async"
                                    onError={(e) => { e.currentTarget.src = `${process.env.PUBLIC_URL}/logo512.png`; }}
                                />
                            ) : (
                                <div className="w-full h-full" />
                            )}
                            <div className="absolute bottom-2 left-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] bg-white text-slate-900 font-semibold shadow-sm">
                                    {r.currency} {(Number(r.price) / 100).toFixed(2)}
                                </span>
                            </div>
                        </div>
                        <div className="p-3">
                            <div className="text-sm font-semibold line-clamp-2 min-h-[2.5rem]">{r.displayName || r.name}</div>
                            {onView && (
                                <button onClick={(e) => { e.stopPropagation(); onView(r); }} className="mt-2 w-full text-xs btn-ghost">View</button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default RecommendationsCarousel;

